import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';

// Services
import { LanguagesService } from '@generated/services/languages.service';
import { ModelsService } from '@generated/services/models.service';
import { NotificationService } from '@core/services/notification.service';

// Models
import {
  LanguageLocaleDto,
  GetModelsHierarchyResult
} from '@generated/models';

@Component({
  selector: 'app-language-locales',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    ButtonModule,
    TableModule,
    InputTextModule,
    ProgressSpinnerModule,
    MessageModule
],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './language-locales.html',
  styleUrl: './language-locales.css'
})
export class LanguageLocalesComponent implements OnInit {
  private languagesService = inject(LanguagesService);
  private modelsService = inject(ModelsService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  // Data
  languageLocales: LanguageLocaleDto[] = [];
  modelsHierarchy: GetModelsHierarchyResult | null = null;

  // Loading states
  isLoading = false;

  // Search
  searchQuery = '';

  get filteredLanguageLocales(): LanguageLocaleDto[] {
    if (!this.searchQuery.trim()) {
      return this.languageLocales;
    }

    const query = this.searchQuery.toLowerCase();
    return this.languageLocales.filter(ll =>
      ll.language_name?.toLowerCase().includes(query) ||
      ll.locale_name?.toLowerCase().includes(query)
    );
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.languagesService.apiLanguagesLanguageLocalesGet().subscribe({
      next: (data) => {
        this.languageLocales = data.language_locales || [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load data:', error);
        this.notificationService.showError('Failed to load data');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });

    this.modelsService.apiModelsHierarchyGet().subscribe({
      next: (data) => {
        this.modelsHierarchy = data;
      }
    });
  }

  getTtsModelName(versionId?: string): string {
    if (!versionId || !this.modelsHierarchy?.model_types) return '-';

    const ttsModelType = this.modelsHierarchy.model_types.find(mt => mt.code === 'tts');
    if (!ttsModelType?.gateways) return '-';

    for (const gateway of ttsModelType.gateways) {
      for (const model of gateway.models || []) {
        for (const version of model.versions || []) {
          if (version.id === versionId) {
            const author = model.author?.name ? `${model.author.name} ` : '';
            return `${gateway.name} - ${author}${model.name} - ${version.name}`;
          }
        }
      }
    }

    return '-';
  }

  onAddClick(): void {
    this.router.navigate(['/admin/language-locales/new']);
  }

  onEditClick(languageLocale: LanguageLocaleDto): void {
    if (languageLocale.id) {
      this.router.navigate(['/admin/language-locales', languageLocale.id, 'edit']);
    }
  }
}
